import { isNullish, fetchWithRelationships, objectFilter, nullishFilter, diffObjectsDeep, objectMap as objectMap$1, objectKeys, WEEK } from '@caresend/utils';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { reactive, watch, set } from 'vue';
import { maybeGetDecryptedItems } from '../dbSync/encryption.js';
import { ls } from '../persist/persist.js';
import { isDbSyncMeta, isRelationshipRef, dbSync } from './dbHelpers.js';
import { itemWithComputedToRaw, isItemWithComputed, setNestedChildOnRecord } from './helper.js';

/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint sort-keys-fix/sort-keys-fix: "error" */
/** Type assertion is safe but support should be also added to @caresend/utils */
const objectMap = objectMap$1;
/**
 * Default time to live for a persistent record in milliseconds.
 */
const DEFAULT_TTL = WEEK;
/**
 * The record manager is a utility for managing records in a reactive store.
 * It provides methods for getting, setting, and unsetting records, and
 * getting and setting child records.
 *
 * @param options - The metadata for database synchronization.
 *
 * A type definition is not required for the metadata object, but by default will
 * support the `path` property to be used in the `set` method for any record manager.
 * For any item added to the record manager where the `path` is provided, it will
 * be prioritized over the `pathGetter` function provided in the `meta` argument.
 *
 * To configure additional metadata you intend to expect when adding records,
 * extend the `BaseRecordMetadata` type on the respective record manager's declaration.
 *
 * @returns The record manager.
 */
const recordManager = (options) => {
    return (computedGetters, getters) => {
        /** Initialize the record of items with computed values */
        const recordWithComputeds = (options === null || options === void 0 ? void 0 : options.persist)
            ? ls.reactive(options.context, {})
            : reactive({});
        const recordMetadata = (options === null || options === void 0 ? void 0 : options.persist)
            ? ls.reactive(`${options.context}[metadata]`, {})
            : reactive({});
        /** Initialize the record of items without computed values */
        const recordRaw = reactive({});
        /**
         * Initialize an object of cache metadata organized by recordID for when utilizing persistence.
         * This will manage clean up records which have passed their expiration date.
         */
        const cacheMeta = (options === null || options === void 0 ? void 0 : options.persist)
            ? ls.reactive(`${options.context}[cache-meta]`, {})
            : reactive({});
        /**
         * Clean up the cache meta object on initialization to remove expired records.
         */
        const cleanUpCache = () => {
            Object.entries(cacheMeta).forEach(([id, recordCacheMeta]) => {
                if (!recordCacheMeta)
                    return;
                if (recordCacheMeta.expires < Date.now()) {
                    delete recordRaw[id];
                    delete recordWithComputeds[id];
                    delete cacheMeta[id];
                    set(recordRaw, id, null);
                    set(recordWithComputeds, id, null);
                    set(cacheMeta, id, null);
                }
            });
        };
        cleanUpCache();
        const watches = new Map();
        /** Watch for changes in the record of items with computed values */
        watch(() => ({ ...recordWithComputeds }), () => {
            objectMap(recordWithComputeds, (item, id) => {
                set(recordRaw, id, itemWithComputedToRaw(item));
            });
        }, { deep: true, immediate: true });
        /** Initialize the computed values for each record */
        const initComputeds = (id, item) => objectMap(computedGetters, (fn) => !isNullish(item) ? fn(id) : null);
        /**
         * Set a record in the store and set with default computed values
         */
        const setRecordItem = (id, value, metadata) => {
            if (value === null) {
                set(recordWithComputeds, id, null);
                set(recordRaw, id, null);
                set(recordMetadata, id, null);
                return;
            }
            const raw = isItemWithComputed(value)
                ? itemWithComputedToRaw(value)
                : value;
            set(recordRaw, id, raw);
            if (raw === null) {
                set(recordWithComputeds, id, null);
                set(recordMetadata, id, null);
                return;
            }
            const withComputed = {
                ...raw,
                computed: initComputeds(id, raw),
            };
            set(recordWithComputeds, id, withComputed);
            set(recordMetadata, id, metadata);
            if (options === null || options === void 0 ? void 0 : options.persist) {
                set(cacheMeta, id, { expires: Date.now() + (options.ttl || DEFAULT_TTL) });
            }
        };
        /**
         * If a computed getter is called with no extraneous arguments, return the value
         * last computed for this record. This ensures the provision of derivative values
         * in both methodologies is functionally identical.
         */
        const cacheMappedComputedGetters = objectMap(computedGetters, (fn, name) => (_id, ...args) => {
            var _a, _b;
            try {
                if (isNullish(_id))
                    return fn(_id, ...args);
                const id = typeof _id === 'string' ? _id : _id.id;
                const cached = (_b = (_a = recordWithComputeds[id]) === null || _a === void 0 ? void 0 : _a.computed) === null || _b === void 0 ? void 0 : _b[name];
                if (args.length === 0 && cached)
                    return cached;
                const computed = fn(id, ...args);
                return computed;
            }
            catch (error) {
                const label = isDbSyncMeta(options) ? options.label : options === null || options === void 0 ? void 0 : options.context;
                console.error(`[RecordManager]: (${label || 'unknown context'}) Error computing getter for ${label}:${String(name)} [cache] ${String(args)}`, {
                    cause: error,
                    request: _id,
                });
                return null;
            }
        });
        const watchComputedGetters = (id) => {
            objectKeys(computedGetters).map((k) => {
                const watchFn = watch(() => {
                    var _a;
                    try {
                        return (_a = computedGetters[k]) === null || _a === void 0 ? void 0 : _a.call(computedGetters, id);
                    }
                    catch (error) {
                        console.error(`[RecordManager]: (${(options === null || options === void 0 ? void 0 : options.context) || 'unknown context'}) Error computing getter for ${options === null || options === void 0 ? void 0 : options.context}:${String(k)} [watch] ${String(id)}`, { cause: error });
                        return null;
                    }
                }, (newVal) => {
                    var _a, _b, _c;
                    const oldVal = (_b = (_a = recordWithComputeds[id]) === null || _a === void 0 ? void 0 : _a.computed) === null || _b === void 0 ? void 0 : _b[k];
                    if (isEqual(newVal, oldVal))
                        return;
                    setRecordItem(id, {
                        ...recordWithComputeds[id],
                        computed: {
                            ...(_c = recordWithComputeds[id]) === null || _c === void 0 ? void 0 : _c.computed,
                            [k]: newVal,
                        },
                    });
                }, { deep: true, immediate: true });
                watches.set(id, watchFn);
            });
        };
        /** Set a record in the store and initialize the computed values if applicable */
        const setAndWatchGetters = (id, item, metadata) => {
            var _a;
            const isCurrentlyWatching = watches.has(id);
            if (Object.keys(computedGetters).length === 0 || isNullish(item) || isCurrentlyWatching) {
                setRecordItem(id, item, metadata);
                return;
            }
            /** Initialize the computed values for the record */
            const itemComputed = initComputeds(id, item);
            /**
             * If the computed values are the same as the last computed values,
             * set and skip initialization of computed values.
             */
            if (isEqual(itemComputed, (_a = recordWithComputeds[id]) === null || _a === void 0 ? void 0 : _a.computed)) {
                setRecordItem(id, item, metadata);
                return;
            }
            setRecordItem(id, {
                ...item,
                computed: itemComputed,
            }, metadata);
            watchComputedGetters(id);
        };
        /** Set up the manager object for external use */
        const manager = {
            /**
             * Fetch a record from the database to be joined with computed values.
             * Only works if database sync meta is provided.
             * @param id - The id of the record to fetch.
             * @returns The record, or null if it does not exist.
             */
            fetch: async (id, skipError = false) => {
                var _a, _b;
                if (!isDbSyncMeta(options)) {
                    if (!skipError)
                        console.info('[RecordManager]:fetch: This store is not dbSync enabled');
                    return null;
                }
                const path = (_a = options.pathGetter) === null || _a === void 0 ? void 0 : _a.call(options, id, undefined);
                const pathParts = (_b = path === null || path === void 0 ? void 0 : path.split('/')) !== null && _b !== void 0 ? _b : [];
                const maybeDbRef = pathParts.slice(0, -1).join('/');
                if (!isRelationshipRef(maybeDbRef))
                    return null;
                const dbRef = maybeDbRef;
                const writers = { [dbRef]: (data) => manager.update(data) };
                const { [dbRef]: response } = await fetchWithRelationships(dbRef, id, {}, { writers });
                try {
                    // Doing this is necessary until we can ensure that FWR decrypts all parts of the response
                    const maybeDecryptedResponse = await maybeGetDecryptedItems(response);
                    const record = maybeDecryptedResponse === null || maybeDecryptedResponse === void 0 ? void 0 : maybeDecryptedResponse[id];
                    if (isNullish(record)) {
                        if (!skipError)
                            console.info(`[RecordManager]:fetch: Record ${id} not found in ref ${dbRef}`);
                        return null;
                    }
                    setAndWatchGetters(id, record);
                    return record;
                }
                catch (error) {
                    if (!skipError) {
                        console.error(`[RecordManager]:fetch: Error decrypting response for ${dbRef}`, { cause: error });
                    }
                    return null;
                }
            },
            /**
             * Filter the record set with computed values.
             * @param filter - The filter function to apply to the record set.
             * @returns The filtered record set.
             */
            filter: (filter) => objectFilter(recordWithComputeds, filter),
            /**
             * Filter the record set raw, i.e. without computed values.
             * @param filter - The filter function to apply to the record set.
             * @returns The filtered record set.
             */
            filterRaw: (filter) => objectFilter(recordRaw, filter),
            /**
             * Iterate over the record set with computed values.
             * @param callback - The callback function to apply to each record.
             */
            forEach: (callback) => {
                Object.values(recordWithComputeds).forEach((item) => {
                    if (isNullish(item))
                        return;
                    callback(item);
                });
            },
            /**
             * Get a record from the store joined with computed values.
             * @param id - The id of the record to get.
             * @returns The record, or null if it does not exist.
             */
            get: (id) => {
                const item = recordWithComputeds[id];
                if (isNullish(item)) {
                    setRecordItem(id, null);
                    /** TODO: Fetch the record from the database */
                    // manager.fetch(record.id);
                }
                return recordWithComputeds[id];
            },
            /**
             * Get a child of a record from the store.
             * @param id - The id of the record to get the child from.
             * @param key - The key of the child record to get.
             * @returns The child record, or null if it does not exist.
             */
            getChildOf: (id, key) => { var _a; return (_a = manager.get(id)) === null || _a === void 0 ? void 0 : _a[key]; },
            /**
             * Get the metadata for a record.
             * @param id - The id of the record to get the metadata for.
             * @returns The metadata for the record, or null if it does not exist.
             */
            getMetadata: (id) => { var _a; return (_a = recordMetadata[id]) !== null && _a !== void 0 ? _a : null; },
            /**
             * Get a record from the store raw, i.e. without computed values.
             * @param id - The id of the record to get, or a filter function to get
             * records that match the filter.
             * @returns The record, or null if it does not exist.
             */
            getRaw: (id) => {
                const item = recordRaw[id];
                if (isNullish(item))
                    setRecordItem(id, null);
                return recordRaw[id];
            },
            // }, (...args) => wrapErrorHandler(args[0], args[1] as string)) as Getters<G> & Getters<C>,
            /**
             * Get a raw clone of a record.
             * @param id - The id of the record to get.
             * @returns The raw clone of the record, or null if it does not exist.
             */
            getRawClone: (id) => {
                var _a;
                const item = recordRaw[id];
                return (_a = cloneDeep(item)) !== null && _a !== void 0 ? _a : null;
            },
            /**
             * Aggregate reference to all provided getters: computed and otherwise
             */
            // getters: objectMap({
            getters: {
                ...cacheMappedComputedGetters,
                ...getters,
            },
            /**
             * Get length of the record set.
             * @returns The length of the record set.
             */
            length: () => Object.values(recordWithComputeds).filter(nullishFilter).length,
            /**
             * Log the record set.
             */
            log: () => {
                console.table(objectMap(recordWithComputeds, (r) => r));
            },
            /**
             * Overwrite the entire record set.
             * @param items - The new records to set.
             */
            overwrite: (items) => {
                manager.reset();
                manager.update(items);
            },
            /**
             * Reset the entire record set.
             */
            reset: () => {
                Object.keys(recordWithComputeds).forEach((id) => {
                    manager.unset(id);
                });
                if (options === null || options === void 0 ? void 0 : options.persist) {
                    localStorage.removeItem(options.context);
                }
            },
            resetComputed: () => {
                watches.forEach((watchFn) => watchFn());
                Object.keys(recordWithComputeds).forEach((id) => {
                    watchComputedGetters(id);
                });
            },
            /**
             * Set a record in the store.
             * @param item - The record to set.
             * @param writeToDb - Whether to write the record to the database.
             * @param metadata - Metadata for the record. This can be used for anything.
             */
            set: (_item, writeToDb = false, metadata) => {
                var _a, _b;
                /**
                 * Handle an edge case where an irresponsible developer sends a record
                 * with computed values to the set method or tries to write a record
                 * with computed values to the database.
                 */
                const item = isItemWithComputed(_item) ? itemWithComputedToRaw(_item) : _item;
                if (isNullish(item))
                    return;
                const oldItem = manager.getRaw(item.id);
                const diffs = diffObjectsDeep(oldItem, item);
                if (!Object.keys(diffs).length) {
                    if (oldItem && watches.has(item.id))
                        return;
                    watchComputedGetters(item.id);
                    return;
                }
                if (!Object.keys(diffs).length
                    && oldItem
                    && watches.has(item.id))
                    return;
                setAndWatchGetters(item.id, item, metadata);
                if (!writeToDb || !isDbSyncMeta(options))
                    return;
                const path = (_a = metadata === null || metadata === void 0 ? void 0 : metadata.path) !== null && _a !== void 0 ? _a : (_b = options.pathGetter) === null || _b === void 0 ? void 0 : _b.call(options, item.id, undefined, item);
                if (!path)
                    return;
                dbSync(item.id, item, path, options.label, options.context, writeToDb, diffs);
            },
            /**
             * Set a child item of a record in the store.
             * @param id - The id of the record to set the child on.
             * @param key - The key of the child record to set.
             * @param value - The value of the child record to set.
             * @param writeToDb - Whether to write the record to the database.
             */
            setChildOf: (id, key, value, writeToDb = false) => {
                var _a, _b;
                /**
                 * Determine if the key can directly be indexed on the record
                 * i.e. not a nested path
                 */
                const keyIsDirectKey = (k) => { var _a; return !((_a = String(k)) === null || _a === void 0 ? void 0 : _a.includes('/')); };
                if (!recordWithComputeds[id])
                    return;
                let explicit = itemWithComputedToRaw(recordWithComputeds[id]);
                if (keyIsDirectKey(key) && explicit) {
                    explicit[key] = value;
                }
                else if (explicit) {
                    explicit = setNestedChildOnRecord(explicit, key, value);
                }
                const diffs = diffObjectsDeep(manager.getRaw(id), explicit);
                if (explicit)
                    manager.set(explicit);
                if (!writeToDb || !isDbSyncMeta(options))
                    return;
                const metadata = recordMetadata[id];
                const mappedDiffs = objectMap(diffs, (i) => i, (subpath) => {
                    var _a, _b, _c;
                    return (_c = (_a = metadata === null || metadata === void 0 ? void 0 : metadata.path) !== null && _a !== void 0 ? _a : (_b = options.pathGetter) === null || _b === void 0 ? void 0 : _b.call(options, id, subpath, explicit !== null && explicit !== void 0 ? explicit : undefined)) !== null && _c !== void 0 ? _c : id;
                });
                const path = (_a = metadata === null || metadata === void 0 ? void 0 : metadata.path) !== null && _a !== void 0 ? _a : (_b = options.pathGetter) === null || _b === void 0 ? void 0 : _b.call(options, id, key, explicit !== null && explicit !== void 0 ? explicit : undefined);
                if (!path)
                    return;
                dbSync(id, value, path, options.label, options.context, writeToDb, mappedDiffs);
            },
            /**
             * Unset a record from the store.
             * @param id - The id of the record to unset.
             * @param writeToDb - Whether to write the record to the database.
             */
            unset: (id, writeToDb = false) => {
                var _a, _b;
                set(recordWithComputeds, id, null);
                delete recordWithComputeds[id];
                if (!writeToDb)
                    return;
                if (!isDbSyncMeta(options))
                    return;
                if (!(options === null || options === void 0 ? void 0 : options.allowUnset)) {
                    // eslint-disable-next-line no-console
                    console.warn('[RecordManager]:unset: `allowUnset` is false, record will not be deleted from db');
                    return;
                }
                const metadata = recordMetadata[id];
                const item = manager.getRaw(id);
                if (!item)
                    return;
                const path = (_a = metadata === null || metadata === void 0 ? void 0 : metadata.path) !== null && _a !== void 0 ? _a : (_b = options.pathGetter) === null || _b === void 0 ? void 0 : _b.call(options, id, undefined, item);
                if (!path)
                    return;
                dbSync(id, null, path, options.label, options.context, writeToDb);
            },
            /**
             * Update the record set with new records.
             * @param items - The new records to update.
             */
            update: (items) => {
                Object.values(items).forEach((item) => {
                    if (isNullish(item))
                        return;
                    manager.set(item);
                });
            },
        };
        return manager;
    };
};

export { recordManager };
